import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { SEO } from "../components/SEO";

const ContactPage = () => {
  return (
    <div data-testid="contact-page">
      <SEO path="/kapcsolat" />

      {/* Hero */}
      <section className="relative py-8 sm:py-10 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="w-12 h-1 bg-amber-500 mb-3" />
              <h1 className="text-2xl sm:text-3xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Kapcsolat
              </h1>
              <p className="text-stone-300 mt-2 max-w-xl text-sm">
                Vegye fel velünk a kapcsolatot!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Contact Cards */}
            <div className="bg-stone-50 border border-stone-200 p-6 flex items-start space-x-4">
              <div className="w-12 h-12 bg-amber-500 flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Telefon</p>
                <a href="tel:+36703379805" className="font-semibold text-slate-900 hover:text-amber-600 transition-colors" data-testid="contact-phone-link">+36 70 337 9805</a>
              </div>
            </div>

            <div className="bg-stone-50 border border-stone-200 p-6 flex items-start space-x-4">
              <div className="w-12 h-12 bg-amber-500 flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Email</p>
                <a href="mailto:info@brsterasz.hu" className="font-semibold text-slate-900 hover:text-amber-600 transition-colors" data-testid="contact-email-link">info@brsterasz.hu</a>
              </div>
            </div>

            <div className="bg-stone-50 border border-stone-200 p-6 flex items-start space-x-4">
              <div className="w-12 h-12 bg-amber-500 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Cím</p>
                <a href="https://www.google.com/maps?q=47.74872,18.07261" target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-900 hover:text-amber-600 transition-colors" data-testid="contact-address">Komárom, Móra Ferenc utca 4</a>
              </div>
            </div>
          </div>

          {/* Info row + Quote CTA */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Kapcsolattartó</h3>
                <p className="text-slate-600 text-sm">Boros György - ügyvezető</p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Szolgáltatási terület</h3>
                <p className="text-slate-600 text-sm">Egész <strong>Magyarország</strong> területén vállalunk munkát. Ingyenes helyszíni felmérést biztosítunk.</p>
              </div>
            </div>

            <div className="bg-stone-50 border border-stone-200 p-8">
              <div className="w-12 h-1 bg-amber-500 mb-5" />
              <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Szeretne árajánlatot kérni?
              </h3>
              <p className="text-slate-600 text-sm mb-6">
                Töltse ki részletes ajánlatkérő űrlapunkat, és hamarosan személyre szabott árajánlatot küldünk Önnek.
              </p>
              <Button
                asChild
                className="rounded-none px-8 py-5 bg-amber-600 text-white hover:bg-amber-700 font-medium text-sm tracking-wider uppercase"
                data-testid="contact-quote-button"
              >
                <Link to="/arajanlat-keres">
                  Ajánlatkérés
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Us */}
      <section className="py-12 sm:py-16 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-12 h-1 bg-amber-500 mb-6" />
              <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Rólunk</h2>
              <div className="space-y-4 text-slate-600 text-sm">
                <p>A <strong>BRS Terasz</strong> alumínium teraszok, teraszfedések, árnyékolók és kocsibeállók tervezésével és kivitelezésével foglalkozik. Célunk, hogy ügyfeleink számára esztétikus, hosszú távon is értéket teremtő megoldásokat hozzunk létre.</p>
                <p>Több éves tapasztalattal rendelkezünk az alumínium szerkezetek gyártásában és telepítésében. Minden projektet egyedileg kezelünk - a helyszíni felméréstől kezdve a tervezésen át egészen a precíz kivitelezésig.</p>
                <p className="font-medium text-slate-900">Minőség, precizitás, megbízhatóság - ezek az értékek vezetnek bennünket.</p>
              </div>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img src="/images/ref-uveg-1.jpg" alt="BRS Terasz munkák" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="elerhetosegek" className="py-12 sm:py-16 bg-amber-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Kérdése van?
          </h2>
          <p className="text-amber-100 mb-8">Hívjon minket vagy küldjön emailt, és mielőbb felvesszük Önnel a kapcsolatot.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="rounded-none px-10 py-6 bg-white text-amber-600 hover:bg-stone-100 font-medium text-sm tracking-wider uppercase">
              <a href="tel:+36703379805"><Phone className="w-4 h-4 mr-2" />Hívjon most</a>
            </Button>
            <Button asChild variant="outline" className="rounded-none px-10 py-6 border-white text-white hover:bg-white/10 font-medium text-sm tracking-wider uppercase">
              <a href="mailto:info@brsterasz.hu"><Mail className="w-4 h-4 mr-2" />Írjon emailt</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
